import type { inferRouterOutputs } from "@trpc/server";
import { Crown } from "lucide-react";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router";

import type { AppRouter } from "@runcheetah/api/router";
import { cn } from "@runcheetah/ui";

import { trpc } from "@/lib/trpc";

import { AssetCreationUpgradeModal } from "./asset-creation-upgrade-modal";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type BillingPlan = RouterOutputs["billing"]["listPlans"][number];

const formatNumber = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale).format(value);

const getMonthlyRunAllowance = (plan: Pick<BillingPlan, "monthlyCreditAllowance"> | null) =>
  plan?.monthlyCreditAllowance ?? 0;

export function FloatingRunsBalance({
  brandId,
  variant = "standalone",
}: {
  brandId: string | undefined;
  variant?: "standalone" | "toolbar";
}) {
  const intl = useIntl();
  const location = useLocation();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const returnPath = `${location.pathname}${location.search}${location.hash}`;
  const billingAvailabilityQuery = trpc.brand.getBillingAvailability.useQuery(
    { brandId: brandId ?? "" },
    { enabled: Boolean(brandId), staleTime: 60_000 },
  );
  const billingOverviewQuery = trpc.brand.getBillingOverview.useQuery(
    { brandId: brandId ?? "" },
    {
      enabled: Boolean(brandId && billingAvailabilityQuery.data?.enabled),
      refetchOnWindowFocus: true,
      staleTime: 30_000,
    },
  );
  const billingPlansQuery = trpc.billing.listPlans.useQuery(undefined, {
    enabled: Boolean(brandId && billingAvailabilityQuery.data?.enabled),
    staleTime: 60_000,
  });

  const overview = billingOverviewQuery.data;
  const remaining = overview?.usage.credits.remaining ?? null;
  const currentAllowance = getMonthlyRunAllowance(overview?.plan ?? null);
  const upgradePlans = useMemo(
    () =>
      (billingPlansQuery.data ?? []).filter(
        (plan) => getMonthlyRunAllowance(plan) > currentAllowance,
      ),
    [billingPlansQuery.data, currentAllowance],
  );
  const hasUpgradePlan = upgradePlans.length > 0;

  if (!billingAvailabilityQuery.data?.enabled || remaining === null) {
    return null;
  }

  return (
    <>
      <div
        className={cn(
          "flex shrink-0 justify-end",
          variant === "standalone" && "px-4 pt-3 sm:px-6 lg:px-8",
        )}
      >
        <div
          className={cn(
            "inline-flex max-w-full items-center overflow-hidden rounded-full border border-border/70 bg-card/88 text-xs font-bold text-foreground shadow-[0_14px_34px_-24px_rgba(13,31,45,0.55)] backdrop-blur-md ring-1 ring-white/60",
            variant === "toolbar" && "shadow-none",
            hasUpgradePlan && "divide-x divide-[#0D1F2D]/12",
          )}
        >
          <div className="inline-flex truncate px-3 py-2">
            <span
              className="mr-2 mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C8FF00] shadow-[0_0_0_3px_rgba(200,255,0,0.18)]"
              aria-hidden="true"
            />
            <FormattedMessage
              id="floatingRunsBalance.runsLeft"
              defaultMessage="{count} runs left"
              values={{ count: formatNumber(remaining, intl.locale) }}
            />
          </div>
          {hasUpgradePlan ? (
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1.5 bg-[#0D1F2D] px-3 py-2 text-[#C8FF00] transition hover:bg-[#173044] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0D1F2D]/35"
              onClick={() => setUpgradeOpen(true)}
            >
              <Crown className="h-3.5 w-3.5" aria-hidden="true" />
              <FormattedMessage id="floatingRunsBalance.upgrade" defaultMessage="Upgrade" />
            </button>
          ) : null}
        </div>
      </div>
      <AssetCreationUpgradeModal
        brandId={brandId}
        overview={overview}
        plans={upgradePlans}
        plansPending={billingPlansQuery.isPending || billingPlansQuery.isFetching}
        creditPacks={[]}
        creditPacksPending={false}
        returnPath={returnPath}
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        preferPlans
      />
    </>
  );
}
